import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import { selectAppSettings } from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import Select from 'antd/lib/select';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import {
    DisallowedTokensForVersioningPattern,
    ExternalVersionType,
    ExternalVersioningConfig,
    OtherAllowedTokensForVersioningPattern,
    UserFacingExternalVersionTypes,
    labelForExternalVersionType,
    PossibleDefaultReleaseTypes,
    PatternToken,
    RequireAtLeastOneOfTokensForVersioningPattern,
} from '../../types';
import { displayTextForReleaseType } from '../../helpers/displayHelpers';
import { globalMessage } from '../../utils/globalMessage';
import Switch from 'antd/lib/switch';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { validatePattern } from '../../helpers';
import { colorWithAlpha, rwColors, runwayColors } from '../styles/globalStyles';

interface EditAppSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const patternFieldName = (type: ExternalVersionType) => `externalVersioningConfig_${type}_pattern`;
const placeholderFieldName = (type: ExternalVersionType) => `externalVersioningConfig_${type}_placeholder`;

const subheadForExternalVersionType: Partial<Record<ExternalVersionType, string>> = {
    [ExternalVersionType.AppStore]: 'The version format as it appears in the app store and on your app store builds',
    [ExternalVersionType.RunwayUserFacing]:
        'The version format as it appears within Runway and in Runway notifications',
};

const EditAppSettingsModal: React.FC<EditAppSettingsModalProps> = (props: EditAppSettingsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [showCustomVersioningHelp, setShowCustomVersioningHelp] = React.useState(false);

    const editAppSettingsIsLoading = useIsLoading(AppSettingsUpdatable.UpdateVersioningDefaults);
    const editAppSettingsError = useError(AppSettingsUpdatable.UpdateVersioningDefaults);
    const editAppSettingsSuccess = useUpdated(AppSettingsUpdatable.UpdateVersioningDefaults);

    const defaultReleaseType = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).defaultReleaseType,
    );
    const assumePointReleasesAreHotfixes = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).assumePointReleasesAreHotfixes,
    );
    const defaultReleaseIncrement = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).defaultReleaseIncrement,
    );
    const externalVersioningConfigForType = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).externalVersioningConfigForType,
    );

    const buildExternalVersioningConfigForType = (
        values: Record<string, string>,
    ): Partial<Record<ExternalVersionType, ExternalVersioningConfig>> | undefined => {
        const result: Partial<Record<ExternalVersionType, ExternalVersioningConfig>> = {};

        for (const type of UserFacingExternalVersionTypes) {
            const pattern = values[patternFieldName(type)]?.trim();
            if (pattern) {
                const placeholder = values[placeholderFieldName(type)]?.trim();
                result[type] = {
                    pattern,
                    ...(placeholder ? { allowsUnresolvedInputsWithPlaceholder: placeholder } : {}),
                };
            }
        }

        // Sync AppStoreBuilds from AppStore when AppStoreBuilds is either unset or
        // was previously equal to the AppStore config (i.e. tracking it automatically).
        const newAppStoreConfig = result[ExternalVersionType.AppStore];
        const oldAppStoreConfig = externalVersioningConfigForType?.[ExternalVersionType.AppStore];
        const oldAppStoreBuildsConfig = externalVersioningConfigForType?.[ExternalVersionType.AppStoreBuilds];

        const appStoreBuildsIsUnset = !oldAppStoreBuildsConfig;
        const appStoreBuildsMatchedAppStore =
            oldAppStoreBuildsConfig?.pattern === oldAppStoreConfig?.pattern &&
            oldAppStoreBuildsConfig?.allowsUnresolvedInputsWithPlaceholder ===
                oldAppStoreConfig?.allowsUnresolvedInputsWithPlaceholder;

        if (appStoreBuildsIsUnset || appStoreBuildsMatchedAppStore) {
            if (newAppStoreConfig) {
                result[ExternalVersionType.AppStoreBuilds] = newAppStoreConfig;
            }
            // If AppStore was cleared, AppStoreBuilds is also omitted (cleared)
        } else {
            // AppStoreBuilds was independently configured — preserve it
            if (oldAppStoreBuildsConfig) {
                result[ExternalVersionType.AppStoreBuilds] = oldAppStoreBuildsConfig;
            }
        }

        return Object.keys(result).length > 0 ? result : undefined;
    };

    const editAppSettings = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateVersioningDefaults, {
                orgId: props.orgId,
                appId: props.appId,
                defaultReleaseType: values.defaultReleaseType,
                assumePointReleasesAreHotfixes: values.assumePointReleasesAreHotfixes,
                defaultReleaseIncrement: values.defaultReleaseIncrement,
                externalVersioningConfigForType: buildExternalVersioningConfigForType(values),
            }),
        );
    };

    React.useEffect(() => {
        if (editAppSettingsError) {
            globalMessage.error(editAppSettingsError);
        }
    }, [editAppSettingsError]);

    React.useEffect(() => {
        if (editAppSettingsSuccess) {
            globalMessage.success('Versioning defaults updated!');

            props.onCancel();
        }
    }, [editAppSettingsSuccess]);

    React.useEffect(() => {
        if (!props.open) return;
        const initialValues: Record<string, unknown> = {
            defaultReleaseType,
            assumePointReleasesAreHotfixes,
            defaultReleaseIncrement,
        };
        for (const type of UserFacingExternalVersionTypes) {
            const config = externalVersioningConfigForType?.[type];
            initialValues[patternFieldName(type)] = config?.pattern ?? '';
            initialValues[placeholderFieldName(type)] = config?.allowsUnresolvedInputsWithPlaceholder ?? '';
        }
        form.setFieldsValue(initialValues);
    }, [props.open]);

    return (
        <Modal
            title="Edit version settings"
            okButtonProps={{ loading: editAppSettingsIsLoading }}
            onOk={() => form.submit()}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                form.resetFields();
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text>
                    These settings are used by Runway to differentiate release types and when predicting upcoming
                    versions or automatically creating new releases.
                </Text>
                <Form
                    onFinish={editAppSettings}
                    initialValues={{
                        defaultReleaseType,
                        assumePointReleasesAreHotfixes,
                        defaultReleaseIncrement,
                    }}
                    onValuesChange={(changedValues) => {
                        for (const type of UserFacingExternalVersionTypes) {
                            if (patternFieldName(type) in changedValues) {
                                form.validateFields([placeholderFieldName(type)]);
                            }
                        }
                    }}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="defaultReleaseType"
                        label="Default release type"
                        rules={[
                            {
                                required: true,
                                message: 'Choose a default release type.',
                            },
                        ]}
                        style={{ width: 300 }}
                        labelAlign={'left'}
                    >
                        <Select placeholder={'Select'} autoFocus>
                            {PossibleDefaultReleaseTypes.map((releaseType, index) => {
                                return (
                                    <Select.Option key={index} value={releaseType}>
                                        {displayTextForReleaseType(releaseType)}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="defaultReleaseIncrement"
                        label={
                            <span>
                                Default release increment{' '}
                                <TooltipInfoIcon tooltipContent="How much the default release type's digit increases when predicting the next version, e.g. an increment of 2 predicts 1.4.0 after 1.2.0. With an increment above 1, the next release isn't automatically created after a roll-forward hotfix." />
                            </span>
                        }
                        rules={[{ required: true, message: 'Enter a default release increment.' }]}
                        style={{ width: 300, paddingTop: 12 }}
                    >
                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="assumePointReleasesAreHotfixes"
                        label="Assume point releases are hotfixes"
                        valuePropName="checked"
                        style={{ paddingTop: 12 }}
                    >
                        <Switch disabled={false} />
                    </Form.Item>

                    <Divider />

                    <div style={{ marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 15 }}>
                            Custom versioning
                        </Text>
                    </div>

                    <Text>
                        The optional settings below allow you to customize the format of versions as they appear in
                        certain contexts, if different than standard three-digit SemVer. For example...
                        {!showCustomVersioningHelp && (
                            <>
                                {' '}
                                <Link onClick={() => setShowCustomVersioningHelp(true)}>Show more</Link>
                            </>
                        )}
                        {showCustomVersioningHelp && (
                            <>
                                {' '}
                                if your app store versions drop trailing zeroes (1.2.0 → 1.2), set the app store version
                                pattern to <Text code>{'{versionConcise}'}</Text>. If you substitute a build number for
                                the patch digit, use <Text code>{'{versionMajorMinor}.{ciBuildNumber}'}</Text>.
                                <br />
                                <br />
                                Note that these settings are entirely independent of other settings where version format
                                may also be specified, e.g. branch and tag patterns.
                                <br />
                                <br />
                                More complex configurations are available upon request - contact Runway support for
                                assistance. <Link onClick={() => setShowCustomVersioningHelp(false)}>Show less</Link>
                            </>
                        )}
                    </Text>

                    <div style={{ marginTop: 20 }}>
                        {UserFacingExternalVersionTypes.map((type, index) => (
                            <div
                                key={type}
                                style={{ marginBottom: index < UserFacingExternalVersionTypes.length - 1 ? 28 : 16 }}
                            >
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>{labelForExternalVersionType[type]}</Text>
                                </div>
                                {subheadForExternalVersionType[type] && (
                                    <div style={{ marginBottom: 8 }}>
                                        <Text style={{ color: runwayColors.darkTextGrey, fontSize: 12 }}>
                                            {subheadForExternalVersionType[type]}
                                        </Text>
                                    </div>
                                )}
                                <Form.Item
                                    name={patternFieldName(type)}
                                    label="Pattern"
                                    style={{ marginBottom: 8 }}
                                    // Also defined on the backend, in ValidateVersioningPattern in lib/common/versioning.go: keep them in sync
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                var trimmed = value?.trim();
                                                if (!trimmed) {
                                                    return Promise.resolve();
                                                }
                                                if (trimmed == PatternToken.Version) {
                                                    return Promise.reject('This pattern duplicates default behavior.');
                                                }
                                                if (
                                                    trimmed.includes(PatternToken.VersionMajorMinor) ||
                                                    trimmed.includes(PatternToken.VersionMajor)
                                                ) {
                                                    const containsAtLeastOneOtherAllowedToken =
                                                        OtherAllowedTokensForVersioningPattern.some((token) =>
                                                            trimmed.includes(token),
                                                        );
                                                    if (!containsAtLeastOneOtherAllowedToken) {
                                                        return Promise.reject(
                                                            'This pattern does not contain enough tokens to be fully resolved.',
                                                        );
                                                    }
                                                }
                                                return validatePattern(
                                                    value,
                                                    labelForExternalVersionType[type],
                                                    RequireAtLeastOneOfTokensForVersioningPattern,
                                                    DisallowedTokensForVersioningPattern,
                                                );
                                            },
                                        },
                                    ]}
                                >
                                    <Input placeholder={`e.g. {versionConcise}`} />
                                </Form.Item>
                                <Form.Item
                                    name={placeholderFieldName(type)}
                                    label={
                                        <span>
                                            Placeholder for unresolved pattern{' '}
                                            <span style={{ marginLeft: 4 }}>
                                                <TooltipInfoIcon
                                                    tooltipContent={
                                                        <>
                                                            Only set this value if your version pattern contains a token
                                                            that may be unresolved at some point during the release
                                                            cycle. For example, with a version pattern like{' '}
                                                            <span
                                                                style={{
                                                                    fontFamily:
                                                                        'ui-monospace, SFMono-Regular, SF Mono, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                                    padding: '1px 6px',
                                                                    borderRadius: 4,
                                                                    backgroundColor: colorWithAlpha(
                                                                        rwColors.primary,
                                                                        0.25,
                                                                    ),
                                                                }}
                                                            >
                                                                {'{versionMajorMinor}.{appStoreBuildNumber}'}
                                                            </span>
                                                            , the build token will be unresolved until a final build
                                                            ships and Runway will fail to form version strings in the
                                                            meantime, unless you set a placeholder value.
                                                        </>
                                                    }
                                                />
                                            </span>
                                        </span>
                                    }
                                    style={{ marginBottom: 0, paddingTop: 8 }}
                                    // Also defined on the backend, in ValidateVersioningPlaceholder in lib/common/versioning.go: keep them in sync
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value?.trim()) {
                                                    return Promise.resolve();
                                                }
                                                if (value.includes('{') || value.includes('}')) {
                                                    return Promise.reject(
                                                        'Placeholder value cannot contain pattern tokens.',
                                                    );
                                                }
                                                const pattern = form.getFieldValue(patternFieldName(type))?.trim();
                                                if (!pattern) {
                                                    return Promise.reject('No pattern is set.');
                                                }
                                                if (
                                                    !pattern.includes(PatternToken.VersionMajorMinor) &&
                                                    !pattern.includes(PatternToken.VersionMajor)
                                                ) {
                                                    return Promise.reject(
                                                        'Placeholder value is only supported for version patterns that may be unresolved.',
                                                    );
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input placeholder="e.g. x" style={{ marginTop: 4 }} />
                                </Form.Item>
                            </div>
                        ))}
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default EditAppSettingsModal;
